import { useMemo } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, DataGrid, useColDefs } from '~components';
import ScheduledActionException from '~features/scheduledActions/interfaces/scheduledActionException';
import ScheduledActionsService from '~features/scheduledActions/services/scheduledActionsService';
import { getService } from '~services/serviceFactory';

interface ScheduledActionExceptionsCardProps {
	scheduledActionId: string;
	organisationId: string;
}

const scheduledActionService = getService(ScheduledActionsService);

const ScheduledActionExceptionsCard = ({
	scheduledActionId,
	organisationId,
}: ScheduledActionExceptionsCardProps) => {
	const { t } = useTranslation();
	const persistenceKey = 'scheduledActionExceptions';

	const {
		data: scheduledActionExceptionsData,
		error: scheduledActionExceptionsError,
		isLoading: scheduledActionExceptionsIsLoading,
	} = useSWR(
		scheduledActionService.getScheduledActionExceptionsCacheKey(scheduledActionId, organisationId),
		([_path, scheduledActionId, organisationId]) => {
			return organisationId ?
					scheduledActionService.getScheduledActionExceptions(scheduledActionId, organisationId)
				:	null;
		}
	);

	const isInitialLoading =
		scheduledActionExceptionsIsLoading && scheduledActionExceptionsData == undefined;
	const hasError = scheduledActionExceptionsError != undefined;

	const cardTitle = (scheduledActionExceptionsData: ScheduledActionException[]) => {
		const count = scheduledActionExceptionsData?.length ?? 0;
		return t('exception.labels.count', { ns: 'entities', count: count });
	};

	const colDefs = useColDefs<ScheduledActionException>();
	const columns = useMemo((): GridColDef<ScheduledActionException>[] => {
		return [
			colDefs.id(undefined, undefined, false),
			colDefs.startDate((value: ScheduledActionException) => dayjs(value.start)),
			colDefs.endDate((value: ScheduledActionException) => dayjs(value.end)),
		];
	}, [colDefs]);

	return (
		<Card title={cardTitle(scheduledActionExceptionsData ?? [])} sx={{ height: '100%' }}>
			<DataGrid
				persistenceKey={persistenceKey}
				disableRowSelectionOnClick
				error={hasError}
				loading={isInitialLoading}
				columns={columns}
				rows={scheduledActionExceptionsData ?? []}
				hideFooterPagination
				hideFooter
				noDataTitle={t('exception.status.noExceptionsFoundTitle', { ns: 'entities' })}
				noDataDescription={t('exception.status.noExceptionsFoundDescription', {
					ns: 'entities',
				})}
				errorTitle={t('exception.status.exceptionsErrorTitle', { ns: 'entities' })}
				errorDescription={t('exception.status.exceptionsErrorDescription', { ns: 'entities' })}
				paginationMode='client'
			/>
		</Card>
	);
};

export default ScheduledActionExceptionsCard;
